import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { saveAuthReturnPath } from '../config/authReturn';

const AuthContext = createContext();
const LEGACY_AUTH_STORAGE_KEYS = ['user', 'oauth_provider'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authErrorKey, setAuthErrorKey] = useState('');
  const authStateVersionRef = useRef(0);

  const clearLegacyAuthStorage = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      LEGACY_AUTH_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
    } catch (error) {
      console.warn('Could not clear legacy auth storage:', error);
    }
  }, []);

  const setUserData = useCallback((userData) => {
    authStateVersionRef.current += 1;
    setUser(userData || null);
    clearLegacyAuthStorage();
  }, [clearLegacyAuthStorage]);

  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      const restoreVersion = authStateVersionRef.current;
      clearLegacyAuthStorage();

      try {
        const response = await fetch('/api/auth/session', {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        });

        const result = await response.json().catch(() => null);

        if (!isMounted || restoreVersion !== authStateVersionRef.current) {
          return;
        }

        if (response.ok && result?.authenticated && result.user?.id) {
          setUser(result.user);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.warn('Could not restore auth session:', error);

        if (isMounted && restoreVersion === authStateVersionRef.current) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    restoreSession();

    return () => {
      isMounted = false;
    };
  }, [clearLegacyAuthStorage]);

  const login = useCallback(async (provider) => {
    try {
      setLoading(true);
      setAuthErrorKey('');
      authStateVersionRef.current += 1;
      clearLegacyAuthStorage();

      if (typeof window !== 'undefined') {
        try {
          saveAuthReturnPath(window.sessionStorage, window.location);
        } catch (error) {
          console.warn('Could not save auth return path:', error);
        }
      }
      
      // Redirect to the appropriate OAuth endpoint
      if (provider === 'google') {
        window.location.href = '/api/auth/google';
      } else if (provider === 'github') {
        window.location.href = '/api/auth/github';
      } else {
        throw new Error('Unsupported provider');
      }
      
      // Note: The loading state will be reset when the page redirects
      // The actual authentication will be handled by the AuthCallback component
      
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
      setAuthErrorKey('loginFailed');
    }
  }, [clearLegacyAuthStorage]);

  const clearAuthError = useCallback(() => {
    setAuthErrorKey('');
  }, []);

  const logout = useCallback(() => {
    setAuthErrorKey('');
    authStateVersionRef.current += 1;
    fetch('/api/auth/logout', {
      method: 'POST',
      keepalive: true
    }).catch((error) => {
      console.warn('Logout session cleanup failed:', error);
    });

    setUser(null);
    clearLegacyAuthStorage();
    // Redirect to homepage after logout
    window.location.href = '/';
  }, [clearLegacyAuthStorage]);

  const updateUser = (updatedData) => {
    const updatedUser = { ...user, ...updatedData };
    setUserData(updatedUser);
  };

  const value = {
    user,
    login,
    logout,
    authErrorKey,
    clearAuthError,
    setUserData,
    updateUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
